import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Settings2, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  VisibilityState, 
  useReactTable, 
  getCoreRowModel, 
  getFilteredRowModel, 
  getSortedRowModel, 
  getPaginationRowModel,
  ColumnDef
} from "@tanstack/react-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { reportsService, Report } from "@/services/reportsService";
import { format } from "date-fns";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface ReportsListProps {
  onEditReport: (report: Report) => void;
}

const categoryColors = {
  Original: "bg-gray-900 text-white",
  Consolidation: "bg-red-500 text-white", 
  Affiliate: "bg-blue-500 text-white",
  "Ad Scores": "bg-orange-500 text-white"
};

const statusColors = {
  Planned: "bg-blue-100 text-blue-800",
  "In Progress": "bg-yellow-100 text-yellow-800",
  Uploaded: "bg-green-100 text-green-800"
};

export function ReportsList({ onEditReport }: ReportsListProps) {
  const queryClient = useQueryClient();
  const [deleteReport, setDeleteReport] = useState<Report | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [yearFilter, setYearFilter] = useState<string>(new Date().getFullYear().toString());
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const { data: allReports = [], isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: () => reportsService.getReports(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Filter reports based on category, search query and year
  const reports = allReports.filter(report => {
    const matchesCategory = categoryFilter === "all" || report.category === categoryFilter;
    const matchesSearch = searchQuery === "" || 
      report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesYear = yearFilter === "all" || 
      (report.delivery_date && new Date(report.delivery_date).getFullYear().toString() === yearFilter);
    
    return matchesCategory && matchesSearch && matchesYear;
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => reportsService.deleteReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report deleted successfully');
      setDeleteReport(null);
    },
    onError: (error) => {
      toast.error('Failed to delete report');
      console.error('Delete error:', error);
    }
  });

  const columns: ColumnDef<Report>[] = [
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ row }: any) => (
        <Badge className={categoryColors[row.original.category as keyof typeof categoryColors]}>
          {row.original.category}
        </Badge>
      ),
    },
    {
      accessorKey: "name",
      header: "Report Name",
    },
    {
      accessorKey: "season",
      header: "Season",
      cell: ({ row }: any) => `Season ${row.original.season}`,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => (
        <Badge variant="outline" className={statusColors[row.original.status as keyof typeof statusColors]}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      cell: ({ row }: any) => 
        row.original.start_date 
          ? format(new Date(row.original.start_date), 'MMM dd, yyyy')
          : '-',
    },
    {
      accessorKey: "delivery_date",
      header: "Delivery Date",
      cell: ({ row }: any) => 
        row.original.delivery_date 
          ? format(new Date(row.original.delivery_date), 'MMM dd, yyyy')
          : '-',
    },
    {
      id: "total_cost",
      header: "Total Cost",
      cell: ({ row }: any) => {
        const report = row.original;
        const externalCosts = report.external_costs as any;
        const totalCost = (report.internal_costs || 0) + 
          (externalCosts?.panel || 0) + 
          (externalCosts?.analyst || 0) + 
          (externalCosts?.data || 0);
        return `€${totalCost.toLocaleString()}`;
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: any) => {
        const report = row.original;
        
        return (
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onEditReport(report)}
              aria-label="Edit report"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setDeleteReport(report)}
              aria-label="Delete report"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: reports,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
  });

  if (isLoading) {
    return <div className="text-center py-8">Loading reports...</div>;
  }

  if (!allReports || allReports.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">No reports found</p>
        <p className="text-sm text-muted-foreground">Create your first report to get started</p>
      </div>
    );
  }

  return (
    <>
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-sm px-3 py-2 border border-input rounded-md text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="Original">Original</SelectItem>
            <SelectItem value="Consolidation">Consolidation</SelectItem>
            <SelectItem value="Affiliate">Affiliate</SelectItem>
            <SelectItem value="Ad Scores">Ad Scores</SelectItem>
          </SelectContent>
        </Select>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            <SelectItem value="2024">2024</SelectItem>
            <SelectItem value="2025">2025</SelectItem>
            <SelectItem value="2026">2026</SelectItem>
          </SelectContent>
        </Select>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" aria-label="Toggle columns">
                  <Settings2 className="h-4 w-4" />
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>Toggle columns</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">No reports match your filters</p>
          <p className="text-sm text-muted-foreground">Try adjusting your search or category filter</p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={reports}
          hideExport={true}
          hideSearch={true}
          hideToggleColumns={true}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteReport} onOpenChange={() => setDeleteReport(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteReport?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (deleteReport) {
                  deleteMutation.mutate(deleteReport.id);
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}